import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';

import { ApiService } from '../../api/common/api.service';
import { DeviceManagerService } from '../../core/services/device-manager.service';
import { ImageAsset } from '../../api/images/image-asset-model';
import { ImageManagerService, ImageSizeResult } from '../../api/images/image-manager.service';

import { StockImage } from '../../api/images/stock-image-model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
	selector: 'mycampapp-select-images',
	templateUrl: './select-images.component.html',
	styleUrls: ['./select-images.component.less']
})
export class SelectImagesComponent implements OnInit, OnDestroy
{
	@HostBinding('class') private get class(): string { return this.shrinkToFit ? 'shrink-to-fit': ''; }

	_photo?: ImageAsset;
	private changedFromUndefined = false;
	@Input() set photo(value: ImageAsset | undefined) {
		const changed = this._photo !== value && (this._photo !== undefined || this.changedFromUndefined);
		this._photo = value;
		this.uploadFile = undefined;
		this.uploadObjectUrl = undefined;
		this.imageRemoved = false;
		if (changed) this.changed.emit();
	}
	get photo(): ImageAsset | undefined { return this._photo; }

	_photoUrl?: string; // As an alternative to photo: ImageAsset
	@Input() set photoUrl(value: string | undefined) {
		this._photoUrl = value;
		this.uploadFile = undefined;
		this.uploadObjectUrl = undefined;
		this.imageRemoved = false;
	}
	get photoUrl(): string | undefined { return this._photoUrl; }

	@Input() disabled = false;
	@Input() minWidth?: number;
	@Input() minHeight?: number;
	@Input() recommendedWidth?: number;
	@Input() recommendedHeight?: number;
	@Input() shrinkToFit = false;
	@Input() allowStockImage = false;

	@Output() changed = new EventEmitter();

	imageRemoved = false;
	selectStockImage = false;
	uploadObjectUrl: any;

	MinimumSizeErrorMessage?: string;

	private uploadFile: any;

	constructor(public deviceManager: DeviceManagerService,
	            private messageService: MessageService,
	            private imageManager: ImageManagerService,
	            private http: HttpClient) { }

	ngOnInit(): void
	{
	}

	ngOnDestroy() {
		if (this.uploadObjectUrl) {
			URL.revokeObjectURL(this.uploadObjectUrl);
		}
	}

	clear(): void
	{
		this._photoUrl = undefined;
		this._photo = undefined;
		this.uploadFile = undefined;
		this.uploadObjectUrl = undefined;
		this.imageRemoved = false;
		this.changedFromUndefined = false;
	}

	get hasFile(): boolean
	{
		return !this.imageRemoved && this.photo != undefined || this.photoUrl != undefined || this.uploadFile != undefined;
	}

	get selectedFile(): File { return this.uploadFile; }

	get imageSize(): number { return this.deviceManager.isMobile ? 100 : 200; }

	onFilesSelected(event: any)
	{
		if (event === undefined) return;
		this.MinimumSizeErrorMessage = undefined;
		this.imageRemoved = false;
		this.uploadFile = event.files[0];
		if (!ApiService.isValidUploadFilename(this.uploadFile.name))
		{
			this.displayError('The filename cannot contain special characters. Please use only letters, numbers, underscores, and dashes.');
			this.uploadFile = undefined;
			return;
		}

		this.changed.emit();
		if (this.minWidth == undefined || this.minHeight == undefined)
		{
			this.getObjectUrl();
			return;
		}

		this.imageManager.checkImageSize(this.uploadFile).subscribe((result: ImageSizeResult) =>
		{
			if (result.width < this.minWidth! || result.height < this.minHeight!)
			{
				this.MinimumSizeErrorMessage = `Image is too small: ${result.width} pixels by ${result.height} pixels. It needs to be at least ${this.minWidth} by ${this.minHeight}.`;
			}

			this.getObjectUrl();
		});
	}

	onRemoveImage(): void
	{
		this.uploadFile = undefined;
		this.uploadObjectUrl = undefined;
		this.imageRemoved = this.photo != undefined || this.photoUrl != undefined;
		this.changed.emit();
	}

	onChooseStockImage(): void
	{
		this.selectStockImage = true;
	}

	async onStockImageSelected(stockImage: StockImage)
	{
		this.selectStockImage = false;
		if (stockImage == undefined) return;

		// Create a file object from the stock image URL
		this.createFileFromUrl(stockImage.Url, stockImage.Name).subscribe({
		next: (file: any) => {
			if (file == undefined) return;
			this.changed.emit();
			this.uploadFile = file;
			this.uploadObjectUrl = URL.createObjectURL(file);
		},
			error: error => {}
		});
	}

	private displayError(text: string): void
	{
		this.messageService.add({ severity: 'error', summary: 'File error', detail: text });
		this._photo = undefined;
		this.uploadFile = undefined;
		this.uploadObjectUrl = undefined;
	}

	private getObjectUrl()
	{
		this.changed.emit();
		this.uploadObjectUrl = this.uploadFile.objectURL;
	}

	createFileFromUrl(imageUrl: string, fileName: string): Observable<File> {
		return this.http.get(imageUrl, { responseType: 'blob' }).pipe(
			map(blob => new File([blob], fileName, { type: blob.type }))
		);
	}
}
